import { useRef } from 'react';
import { useImagePreload } from '../../hooks/useImagePreload';
import { LoadingThumbnail } from './LoadingThumbnail';
import { Thumbnail, ThumbnailProps } from './thumbnail';

interface Props extends ThumbnailProps {
  theme: 'dark' | 'light';
}

/**
 * Renders a thumbnail only after its image has been preloaded.
 * While a new URL is still loading, keeps showing the previously painted thumbnail
 * instead of flashing the black loading spinner.
 */
export function PreloadedThumbnail({ theme, ...rest }: Props) {
  const preloaded = useImagePreload(rest.url);
  const displayPropsRef = useRef<ThumbnailProps | null>(null);

  // Commit the current props only when the image is ready to paint.
  // Until then, keep the last committed props so the UI does not flash the loader.
  if (preloaded) {
    displayPropsRef.current = {
      url: rest.url,
      offset: rest.offset,
      metadata: rest.metadata,
      animated: rest.animated,
      targetHeight: rest.targetHeight,
    };
  }

  if (!displayPropsRef.current) {
    return (
      <LoadingThumbnail metadata={rest.metadata} theme={theme} targetHeight={rest.targetHeight} />
    );
  }

  return <Thumbnail {...displayPropsRef.current} />;
}
